import Vue from 'vue'
import { logger } from '@/utils/logger'
import { existThere } from '@/modules/route/redirect'
import { setItem } from '@/modules/storage/sessionStorage'

/**
 * ブラウザのアラートダイアログを表示する関数 | null
 *
 * 本番環境では null として、アラートダイアログを表示しない
 * */
const showErrorByAlert = ((environment: string | undefined) => {
  if (environment === undefined || environment === 'prd') {
    return null
  }

  return (errorMsg: string): void => {
    window.alert(
      '※このダイアログは本番環境では出現しません。' +
        '検証環境で確認された場合は、可能ならばスクリーンショットなどでご共有お願いいたします 。' +
        '\n\nエラーメッセージ\n' +
        errorMsg
    )
  }
})(process.env.NODE_BUILD_ENV)

/**
 * リダイレクト処理を行う
 * 本番環境以外では、アラートダイアログにもエラーメッセージをダンプする
 * Vueのインスタンスが与えられている場合は、Vue Routerによるリダイレクトを行う
 *
 * @param {string} errorMsg
 * @param {Vue} vm
 */
const redirect = (errorMsg: string, vm?: Vue) => {
  setItem('UNKNOWN_ERROR_HAPPENED', errorMsg)

  if (existThere(location.href, 'top')) {
    return
  }

  if (vm === undefined) {
    window.location.href = `${process.env.BASE_URL}/`
  } else {
    vm.$router.push('/top')
  }
}

/**
 * Vue 管理下のエラーを処理する
 * */
Vue.config.errorHandler = (err, vm, info) => {
  const msg1 = '[errorHandler] Captured in Vue.config.errorHandler'
  const msg2 = `[errorHandler] error name: ${err.name}, error message: ${err.message}`
  const msg3 = `[errorHandler] info: ${info}`
  logger.error(msg1)
  logger.error(msg2)
  logger.error(msg3)
  showErrorByAlert?.(`\n${msg1}\n${msg2}\n${msg3}`)

  redirect(`\n${msg1}\n${msg2}\n${msg3}`, vm)
}

/**
 * error イベントを処理する
 *
 * NOTE: 2024-06-28:
 * ink-helper(ink canvas) のエラー時は、ログ表示以外には何もしない。
 * つまり、リダイレクトしてアプリを強制的に中断させたりせず、ユーザに対して操作を継続させる。
 * ただし、false negative（ink-helper 由来以外のエラーも無視してしまう）の可能性もある。
 * そのため、ink-helper の対応が完了した場合などは、無視する部分は見直したい。
 * */
window.addEventListener('error', event => {
  const msg1 = `[error event] Captured in error EventListener ${event.error}`
  const msg2 = `[error event] error: ${event.error}`
  const msg3 = `[error event] message: ${event.message}`
  logger.error(msg1)
  logger.error(msg2)
  logger.error(msg3)
  showErrorByAlert?.(`\n${msg1}\n${msg2}\n${msg3}`)

  const ignore = ((): boolean => {
    const pageName = window.location.href.split('/').pop()
    const ignorePageList: (string | undefined)[] = [
      'submitPreview',
      'fullDigitalTategaki',
      'fullDigitalYokogaki',
      'teishutsuPreview',
      'fullDigitalTeishutsuTategaki',
      'fullDigitalTeishutsuYokogaki',
    ]
    if (ignorePageList.includes(pageName)) {
      return true
    }

    const phrases = ['querySelectorAll']
    const errorMessage = String(event.error)
    return phrases.every(p => errorMessage.includes(p))
  })()

  if (ignore) {
    logger.error('[error event] ignore error')
    return
  }

  redirect(`\n${msg1}\n${msg2}\n${msg3}`)
})

/**
 * reject された Promise インスタンスが処理されなかった場合に発生するイベントを処理する
 *
 * Vue の管理下の rejected Promise（典型的には v-on ディレクティブ）は Vue.config.errorHandler で処理されるため、
 * このイベントはほとんど発生しないと見込まれる
 * */
window.addEventListener('unhandledrejection', event => {
  const msg1 = '[unhandledrejection event] Captured in unhandledrejection EventListener'
  const msg2 = `[unhandledrejection event] reason: ${event.reason}`
  logger.error(msg1)
  logger.error(msg2)
  showErrorByAlert?.(`\n${msg1}\n${msg2}`)

  redirect(`\n${msg1}\n${msg2}`)
})
